import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Award,
  Brain,
  ChevronRight,
  Computer,
  Cpu,
  DollarSign,
  Gauge,
  GraduationCap,
  Handshake,
  LineChart,
  Map as MapIcon,
  MessageCircleQuestion,
  Shield,
  Sparkles,
  Ticket,
  Users,
  Megaphone,
} from 'lucide-react';
import { useAuth } from '@/contexts/SharedAuthContext';
import { trpc } from '@/lib/trpc';
import { openAssistant } from '@/lib/open-assistant';
import { getAreaBadgeStyle } from '@/lib/brand-colors';
import { useVisibleSections } from '@/hooks/useVisibleSections';
import winclapLogo from '@/assets/winclap-logo.svg';
import supportIAImg from '@/assets/supportIA.png';

/**
 * Home de Internal — orientación para todos los clappers (la idea: un clapper
 * nuevo se onboardea solo). Header hero + grid "bento" de secciones (filtradas
 * por permisos vía useVisibleSections) + callout de supportIA (el FAB de abajo
 * a la derecha). Cada card entra escalonada (tailwindcss-animate) y linkea al
 * PRIMER item que el usuario realmente puede abrir.
 */

interface HomeNode {
  key: string;
  title: string;
  description: string;
  href: string;
  icon: React.ElementType;
  accent: { bg: string; text: string };
  badge?: string;
  /** Card destacada: ocupa 2 columnas en el grid bento. */
  featured?: boolean;
}

const TERMINAL_TICKET_STATUSES = new Set(['CANCELLED', 'RESOLVED', 'COMPLETED', 'WONT_FIX']);
const CLOSED_NOTION_STATES = new Set([
  'completado',
  'resuelto',
  'resolved',
  'cerrado',
  'cancelled',
  'no se corregirá',
]);

/**
 * El cierre real de un ticket vive en notionEstado/kanbanEstado (status queda
 * 'OPEN' para siempre salvo cancelación del usuario) — misma semántica que
 * muestra /tickets/mine.
 */
// `status` va opcional porque la query devuelve Record<string, any>: el backend
// no tipa la respuesta, así que prometer que el campo viene sería mentira.
function isTicketClosed(tk: { status?: string; notionEstado?: string | null; kanbanEstado?: string | null }): boolean {
  if (tk.status && TERMINAL_TICKET_STATUSES.has(tk.status)) return true;
  if (tk.kanbanEstado === 'done') return true;
  return CLOSED_NOTION_STATES.has((tk.notionEstado ?? '').toLowerCase());
}

function NodeCard({ node, index }: { node: HomeNode; index: number }) {
  const Icon = node.icon;
  return (
    <Link
      to={node.href}
      style={{ animationDelay: `${Math.min(index, 10) * 60}ms` }}
      className={
        'group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-5 ' +
        'transition-all duration-300 hover:-translate-y-1 hover:border-foreground/20 hover:shadow-lg ' +
        'animate-in fade-in slide-in-from-bottom-2 fill-mode-both [animation-duration:500ms] ' +
        (node.featured ? 'sm:col-span-2' : '')
      }
    >
      {/* Lavado de gradiente del accent — se intensifica en hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.35] transition-opacity duration-300 group-hover:opacity-60 dark:opacity-[0.08] dark:group-hover:opacity-[0.16]"
        style={{ background: `linear-gradient(135deg, ${node.accent.bg} 0%, transparent 55%)` }}
      />
      <span className="relative flex items-start justify-between gap-3">
        <span className="relative">
          {/* Glow radial detrás del chip del ícono */}
          <span
            aria-hidden
            className="absolute -inset-2 rounded-full opacity-40 blur-lg transition-opacity duration-300 group-hover:opacity-70"
            style={{ backgroundColor: node.accent.bg }}
          />
          <span
            className="relative flex h-11 w-11 items-center justify-center rounded-xl"
            style={{ backgroundColor: node.accent.bg, color: node.accent.text }}
          >
            <Icon className="h-5 w-5" />
          </span>
        </span>
        {node.badge && (
          <span className="shrink-0 rounded-full bg-brand-green/15 px-2 py-0.5 text-[10px] font-semibold text-[#0F766E] dark:text-brand-green">
            {node.badge}
          </span>
        )}
      </span>
      <span className="relative mt-3 flex items-center gap-1.5 text-sm font-semibold leading-tight">
        {node.title}
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-foreground/70" />
      </span>
      <span className="relative mt-1 block text-xs leading-snug text-muted-foreground line-clamp-2">
        {node.description}
      </span>
    </Link>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user, isLoading: authLoading } = useAuth();
  const { peopleGroups, peopleTopLinks, navSections, partnerships, isAccessLoading, flags } =
    useVisibleSections();

  const myTicketsQuery = trpc.tickets.getMyTickets.useQuery(undefined, { enabled: !authLoading });
  const myTickets = myTicketsQuery.data?.data || [];
  const myOpenTickets = myTickets.filter((tk) => !isTicketClosed(tk)).length;

  const userRoles: string[] = user?.roles ?? [];
  const primaryRole = userRoles.find((r) => r !== 'user') ?? userRoles[0] ?? 'user';
  const areaStyle = getAreaBadgeStyle(primaryRole);

  // Cada nodo linkea al PRIMER item que el usuario realmente puede abrir
  // (los items del hook ya respetan roles + denieds de subtabs) — un botón
  // del Home jamás debe terminar en /unauthorized.
  const usersGroup = peopleGroups.find((g) => g.key === 'PeopleHub:users');
  const devicesGroup = peopleGroups.find((g) => g.key === 'PeopleHub:devices');
  const performanceGroup = peopleGroups.find((g) => g.key === 'PeopleHub:performance');
  const iaNativeSection = navSections.find((s) => s.key === 'IANative');
  const finOpsSection = navSections.find((s) => s.key === 'FinOps');
  const communityLink = peopleTopLinks.find((l) => l.href === '/admin/users/community-map');
  const hasFullPerformance = performanceGroup?.items.some((i) => i.href === '/performance/talent-map') ?? false;
  const performanceHref = performanceGroup?.items[0]?.href ?? '/performance/dtalk';

  const nodes: HomeNode[] = [
    {
      key: 'tickets',
      title: t('home.nodes.tickets_title', 'Tickets & Soporte'),
      description: t('home.nodes.tickets_desc', 'Pedile ayuda a IT: equipos, accesos, licencias y cualquier problema técnico.'),
      href: '/tickets/select',
      icon: Ticket,
      accent: { bg: '#E7FAF8', text: '#0F766E' },
      featured: true,
      badge:
        myOpenTickets > 0
          ? t('home.nodes.tickets_badge', '{{count}} abiertos', { count: myOpenTickets })
          : undefined,
    },
    {
      key: 'people',
      title: t('home.nodes.people_title', 'People Landing'),
      description: t('home.nodes.people_desc', 'Todo lo de People: oficinas, beneficios, herramientas de trabajo y cómo funciona Winclap.'),
      href: '/people/landing',
      icon: Users,
      accent: { bg: '#C9ADFA', text: '#3B0764' },
    },
    {
      key: 'learning',
      title: 'Learning',
      description: t('home.nodes.learning_desc', 'Clapper Arcade y los programas de formación de People.'),
      href: '/people/learning',
      icon: GraduationCap,
      accent: { bg: '#F3EEFF', text: '#5B21B6' },
    },
    {
      key: 'brain',
      title: t('home.nodes.winclap_brain'),
      description: t('home.nodes.brain_desc', 'La memoria institucional: preguntale sobre clientes y recibí respuestas con fuentes.'),
      href: '/brain',
      icon: Brain,
      accent: { bg: '#EBF7FF', text: '#0C4A6E' },
      featured: true,
    },
    {
      key: 'skills',
      title: t('home.nodes.skills_title', 'Skills Marketplace'),
      description: t('home.nodes.skills_desc', 'Agentes y skills de IA listos para usar en tu trabajo diario.'),
      href: '/skills-agentes',
      icon: Sparkles,
      accent: { bg: '#F3EEFF', text: '#5B21B6' },
    },
    {
      key: 'performance',
      title: 'Performance',
      description: hasFullPerformance
        ? t('home.nodes.performance_desc_full', 'Evaluaciones EPIC, Mapa de Talento y tu conversación de desarrollo (D-Talk).')
        : t('home.nodes.performance_desc_dtalk', 'Tu conversación de desarrollo (D-Talk) con Flavia, cuando el ciclo esté activo.'),
      href: performanceHref,
      icon: Award,
      accent: { bg: '#FFFAE6', text: '#713F12' },
    },
    ...(communityLink
      ? [{
          key: 'community-map',
          title: t('users.community_map', 'Community Map'),
          description: t('home.nodes.community_desc', 'El mapa de todos los clappers: quién es quién, áreas y líderes.'),
          href: communityLink.href,
          icon: MapIcon,
          accent: { bg: '#E0F2FE', text: '#0C4A6E' },
        }]
      : []),
    ...(usersGroup
      ? [{
          key: 'people-ops',
          title: t('home.nodes.people_ops_title', 'Experience'),
          description: t('home.nodes.people_ops_desc', 'Clappi, el panel de procesos de People, y Oficinas.'),
          href: usersGroup.items[0]?.href ?? '/admin/users/people-panel',
          icon: Shield,
          accent: { bg: '#FEF1EC', text: '#7C2D12' },
        }]
      : []),
    ...(devicesGroup
      ? [{
          key: 'devices',
          title: t('home.nodes.devices_title', 'Dispositivos'),
          description: t('home.nodes.devices_desc', 'Inventario, aprobaciones, pedidos, in/out e info útil.'),
          href: devicesGroup.items[0]?.href ?? '/admin/inventory',
          icon: Computer,
          accent: { bg: '#F0F0F0', text: '#111111' },
        }]
      : []),
    {
      key: 'pricing',
      title: t('home.nodes.pricing_manager'),
      description: t('home.nodes.pricing_desc', 'Creá y calculá pricings de deals; Finance los aprueba desde acá.'),
      href: '/finance/pricing',
      icon: DollarSign,
      accent: { bg: '#FFF9DB', text: '#854D0E' },
    },
    ...(flags.canSeeOwnedMedia
      ? [{
          key: 'owned-media',
          title: t('oml.nav.title', 'Owned Media'),
          description: t('home.nodes.oml_desc', 'Cómo funciona cada cuenta de Owned Media: quién es quién, plataformas, journeys y reglas.'),
          href: '/owned-media',
          icon: Megaphone,
          accent: { bg: '#FDF2F8', text: '#9D174D' },
        }]
      : []),
    ...(partnerships.canView
      ? [{
          key: 'partnerships',
          title: t('home.nodes.partnerships_hub'),
          description: t('home.nodes.partnerships_desc', 'Productos, paquetes y casos de TikTok, Google y Meta.'),
          href: '/partnerships',
          icon: Handshake,
          accent: { bg: '#E7FAF8', text: '#134E4A' },
        }]
      : []),
    ...(finOpsSection && finOpsSection.items.length > 0
      ? [{
          key: 'finops',
          title: 'FinOps',
          description: t('home.nodes.finops_desc', 'Dashboards de costos cloud y herramientas de FinOps.'),
          href: finOpsSection.items[0].href,
          icon: LineChart,
          accent: { bg: '#ECFDF5', text: '#065F46' },
        }]
      : []),
    ...(iaNativeSection && iaNativeSection.items.length > 0
      ? [{
          key: 'ia-native',
          title: t('home.nodes.ia_native'),
          description: t('home.nodes.ia_native_desc', 'Suscripciones de IA y observabilidad de costos.'),
          href: iaNativeSection.items[0].href,
          icon: Cpu,
          accent: { bg: '#F3EEFF', text: '#3B0764' },
        }]
      : []),
    // Solo admins: el hook ya lo filtra (misma audiencia que /admin/internal)
    ...(navSections.some((s) => s.key === 'InternalPanel')
      ? [{
          key: 'internal-panel',
          title: t('home.nodes.panel_de_internal'),
          description: t('home.nodes.internal_panel_desc', 'Adopción, tráfico, accesos y salud de la plataforma.'),
          href: '/admin/internal',
          icon: Gauge,
          accent: { bg: '#E7FAF8', text: '#0F766E' },
        }]
      : []),
  ];

  return (
    <div className="relative mx-auto max-w-5xl space-y-8">
      {/* Glow de marca muy sutil detrás del hero */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 h-72 w-2xl -translate-x-1/2 rounded-full opacity-[0.10] blur-3xl dark:opacity-[0.07]"
        style={{ background: 'radial-gradient(closest-side, #1CC9B9 0%, #CEABFF 55%, transparent 100%)' }}
      />

      {/* Hero header */}
      <div className="relative border-b border-border pb-6">
        <p className="mb-2 flex items-center gap-2 text-[10px] font-medium uppercase tracking-widest text-brand-gray3">
          <img src={winclapLogo} alt="Winclap" className="h-3.5 w-auto dark:invert" />
          {/* i18n-ok: marca */}
          Internal ✦
        </p>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-3xl font-normal text-brand-gray1 dark:text-white">
              {authLoading
                ? '...'
                : t('home.greeting', '¡Hola, {{name}}!', {
                    name: user?.name?.split(' ')[0] || 'Clapper',
                  })}
            </h2>
            <span
              aria-hidden
              className="mt-2 block h-1 w-24 rounded-full bg-linear-to-r from-brand-green via-brand-blue to-brand-purple"
            />
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {t(
                'home.intro',
                'Internal es el back office de Winclap: acá te autogestionás — pedidos a IT, todo lo de People, la memoria de clientes y las herramientas de IA. El mapa de abajo muestra solo los apartados que vos podés usar según tu rol.'
              )}
            </p>
          </div>
          {!authLoading && primaryRole && (
            <span
              className="mt-1 shrink-0 rounded-full px-2.5 py-1 text-xs font-medium"
              style={{ backgroundColor: areaStyle.bg, color: areaStyle.text }}
            >
              {primaryRole}
            </span>
          )}
        </div>
      </div>

      {/* Grid bento de secciones */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {nodes.map((node, i) => (
          <NodeCard key={node.key} node={node} index={i} />
        ))}
        {isAccessLoading && (
          <>
            <div className="h-32 animate-pulse rounded-2xl border border-border bg-muted/40" />
            <div className="h-32 animate-pulse rounded-2xl border border-border bg-muted/40" />
          </>
        )}
      </div>

      {/* Callout supportIA — glass */}
      <div className="relative flex items-center gap-4 overflow-hidden rounded-2xl border border-border bg-card/70 p-4 shadow-xs backdrop-blur-md sm:p-5">
        <span
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-14 h-40 w-40 rounded-full opacity-20 blur-2xl"
          style={{ backgroundColor: '#1CC9B9' }}
        />
        <img
          src={supportIAImg}
          alt="supportIA"
          className="h-12 w-12 shrink-0 rounded-full object-cover shadow-xs"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {t('home.assistant_title', '¿Perdido? supportIA te guía')}
          </p>
          <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
            {t(
              'home.assistant_desc',
              'Es el asistente de Internal: consultá inventario, tickets, usuarios o pedile que haga cosas por vos. Vive siempre acá abajo a la derecha 👉'
            )}
          </p>
        </div>
        <button
          onClick={() => openAssistant()}
          className="relative inline-flex shrink-0 items-center gap-2 rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background shadow-xs transition-all hover:opacity-90 hover:shadow-md"
        >
          <MessageCircleQuestion className="h-3.5 w-3.5" />
          {t('home.assistant_cta', 'Abrir supportIA')}
        </button>
      </div>
    </div>
  );
}
